import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

interface EnabledFeature {
  flag_key: string
  name: string
  description: string | null
  enabled_via: 'user_override' | 'user_disabled' | 'global_default'
}

// define all available flag keys here
export enum FeatureFlagKey {
  AddEntries = 'calendar.add_entries',
  AddWorkouts = 'calendar.add_workouts',
  AddAllDayEvents = 'calendar.add_all_day_events',
  GenerateWorkouts = 'calendar.generate_workouts',
  CalDavSharing = 'calendar.caldav',
  CalendarView = 'calendar',
  Testing = 'testing',
  Races = 'races',

  AthleteCalendar = 'athlete.calendar',
  AthleteTesting = 'athlete.testing',
  AthleteRaces = 'athlete.races',

  // Coach features
  CoachAnalysis = 'coach.analysis',
  CoachAutomations = 'coach.automations',

  // Workout Analysis features
  WBalChart = 'workout_analysis.wbal_chart',
}

export const useFeatureFlags = () => {
  const { user } = useAuth()
  const [enabledFeatures, setEnabledFeatures] = useState<EnabledFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEnabledFeatures = useCallback(async () => {
    if (!user) {
      setEnabledFeatures([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: dbError } = await supabase.rpc('get_user_enabled_features' as any, {
        check_user_id: user.id,
      })

      if (dbError) {
        console.error('Error fetching enabled features:', dbError)
        setError('Failed to load feature flags')
        return
      }

      setEnabledFeatures(data || [])
    } catch (err) {
      console.error('Exception fetching enabled features:', err)
      setError('Failed to load feature flags')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchEnabledFeatures()
  }, [fetchEnabledFeatures])

  const isFeatureEnabled = useCallback(
    (flagKey: FeatureFlagKey): boolean => {
      return enabledFeatures.some((feature) => feature.flag_key === flagKey)
    },
    [enabledFeatures]
  )

  return {
    enabledFeatures,
    loading,
    error,
    isFeatureEnabled,
    refetch: fetchEnabledFeatures,
  }
}
